import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import {
  ReactiveFormsModule,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  DeleteConversionTarget,
  MbrsStatusEnum,
} from '../../../../types/commons';
import { FormSubmitService } from '../../../services/mbrs/form-submit.service';
import { tap } from 'rxjs';
import { Router } from '@angular/router';
import { Dialog } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { AddSpacesPipe } from '../../../utils/pipes/add-spaces.pipe';
import { CommonModule } from '@angular/common';
import { MessageService } from 'primeng/api';
import { ConversionListingService } from '../../../services/common/conversion-listing.service';
import {
  TOAST_DURATION_LONG_MS,
} from '../../../utils/toast-durations.util';
import { OverflowTooltipDirective } from '../../../directives/overflow-tooltip.directive';
import { InteractiveToastComponent } from '../interactive-toast/interactive-toast.component';
import { conversionActionFailureCopy } from '../../../utils/conversion-error-codes.util';

@Component({
  selector: 'app-delete-conversion',
  imports: [
    Dialog,
    ConfirmDialogModule,
    ButtonModule,
    TooltipModule,
    OverflowTooltipDirective,
    AddSpacesPipe,
    CommonModule,
    ReactiveFormsModule,
    InteractiveToastComponent,
  ],
  templateUrl: './delete-conversion.component.html',
  styleUrl: './delete-conversion.component.css',
})
export class DeleteConversionComponent {
  @Input() conversionData!: DeleteConversionTarget;
  @Input() isArchiveDialogOpen = false;
  @Output() isArchiveDialogOpenChange = new EventEmitter<boolean>(false);
  @Output() archiveConversionCompleted = new EventEmitter<void>();

  archiveFormGroup = new FormGroup({
    conversionId: new FormControl('', Validators.required),
  });
  MbrsStatusEnum = MbrsStatusEnum;
  isArchiving: boolean = false;
  formSubmitService = inject(FormSubmitService);
  messageService = inject(MessageService);
  conversionListingService = inject(ConversionListingService);
  router = inject(Router);

  toggleArchiveConversionDialog(isOpen: boolean): void {
    this.isArchiveDialogOpen = isOpen;
    this.isArchiveDialogOpenChange.emit(isOpen);
  }

  archiveConversion() {
    this.archiveFormGroup.patchValue({
      conversionId: this.conversionData.id,
    });

    // Drives the Delete button's [loading]/[disabled] bindings so a slow request
    // can't be double-submitted. Reset in tap.next for both outcomes below.
    this.isArchiving = true;

    this.formSubmitService
      .archiveConversion(this.archiveFormGroup.value)
      .pipe(
        tap({
          next: (response) => {
            this.isArchiving = false;
            if (response.status) {
              this.messageService.add({
                key: 'process_mbrs_data',
                severity: 'custom-success',
                summary: `Conversion deleted successfully!`,
                life: TOAST_DURATION_LONG_MS,
              });

              this.archiveConversionCompleted.emit();
            } else {
              // Non-200: FormSubmitService.createErrorHandler swallows the HTTP
              // fault into { status: false }, so it arrives here (not the error
              // callback). The backend did NOT delete — keep the caller on its
              // current page and do NOT refresh (archiveConversionCompleted stays
              // unemitted, so neither the listing's onStatusChange refresh nor the
              // conversion page's onDeletionCompleted navigation fires). The dialog
              // still closes below.
              //
              // The error toast is routed through THIS component's OWN outlet
              // (`delete_conversion_error`, declared at the root of this template)
              // rather than the host's `process_mbrs_data` outlet. Reason: hosts
              // differ in which MessageService instance is in scope — the listing
              // (XbrlFilesComponent) provides one, but on the conversion form the
              // nearest provider is role0/role1, whose only toast outlet is keyless,
              // so a `process_mbrs_data` message is silently dropped there. A
              // self-owned outlet shares this component's injected MessageService on
              // every host, so the error toast renders identically everywhere.
              this.messageService.add({
                key: 'delete_conversion_error',
                severity: 'custom-error',
                summary:
                  conversionActionFailureCopy(response.errorCode) ||
                  'There was an issue deleting the XBRL Conversion File. Please try again later.',
                life: TOAST_DURATION_LONG_MS,
              });
            }
          },
        })
      )
      .subscribe({
        next: () => {
          this.toggleArchiveConversionDialog(false);
        },
      });
  }
}
